import { polarToVec } from "../../../core/util/MathUtil";
import { V, V2d } from "../../../core/Vector";
import { BaseEnemy } from "../BaseEnemy";

export interface SteeringResult {
  force: V2d;
  arrived: boolean;
}

/**
 * Simple steering behaviors for enemy movement
 */
export class SteeringBehaviors {
  /**
   * Seek behavior - applies force toward target position
   */
  static seek(enemy: BaseEnemy, target: V2d, maxForce: number): SteeringResult {
    if (!enemy.body) return { force: V(0, 0), arrived: false };

    const position = V(enemy.body.position);
    const velocity = V(enemy.body.velocity);

    // Calculate desired velocity (toward target at max speed)
    const desired = target.sub(position).normalize().imul(120); // max speed

    // Calculate steering force
    const steer = desired.sub(velocity);

    // Limit force magnitude
    const forceMagnitude = Math.min(steer.magnitude, maxForce);
    const force = steer.normalize().imul(forceMagnitude);

    return { force, arrived: false };
  }

  /**
   * Wander behavior - smooth random movement with containment
   */
  static wander(
    enemy: BaseEnemy,
    maxForce: number,
    wanderRadius: number = 30,
    wanderDistance: number = 40,
    containmentRadius: number = 200,
  ): SteeringResult {
    if (!enemy.body) return { force: V(0, 0), arrived: false };

    const velocity = V(enemy.body.velocity);
    const position = V(enemy.body.position);
    const distanceFromCenter = position.magnitude;

    // If too far from center, steer back toward center instead of wandering
    if (distanceFromCenter > containmentRadius) {
      const centerPos = V(0, 0);
      return this.seek(enemy, centerPos, maxForce);
    }

    // Get current wander angle or initialize it
    if (!enemy.wanderAngle) {
      enemy.wanderAngle = Math.random() * Math.PI * 2;
    }

    // Update wander angle with small random changes
    const angleChange = (Math.random() - 0.5) * 0.3; // Max change of ~17 degrees
    enemy.wanderAngle += angleChange;

    // Calculate circle center in front of velocity
    const circleCenter = velocity.normalize().imul(wanderDistance);

    // Calculate target point on circle
    const wanderTarget = circleCenter.add(
      polarToVec(enemy.wanderAngle, wanderRadius),
    );

    // Add to current position to get world target
    const worldTarget = position.add(wanderTarget);

    return this.seek(enemy, worldTarget, maxForce);
  }

  /**
   * Separation behavior - avoid crowding neighbors
   */
  static separate(
    enemy: BaseEnemy,
    neighbors: BaseEnemy[],
    maxForce: number,
    separationRadius: number = 50,
  ): SteeringResult {
    if (!enemy.body) return { force: V(0, 0), arrived: false };

    const position = V(enemy.body.position);
    let separationForce = V(0, 0);
    let count = 0;

    // Avoid other enemies
    for (const neighbor of neighbors) {
      if (neighbor === enemy || !neighbor.body || neighbor.isDestroyed())
        continue;

      const neighborPos = V(neighbor.body.position);
      const distance = position.sub(neighborPos).magnitude;

      if (distance > 0 && distance < separationRadius) {
        // Calculate force away from neighbor
        const diff = position.sub(neighborPos).normalize();
        // Weight by distance (closer = stronger force)
        const weight = (separationRadius - distance) / separationRadius;
        separationForce = separationForce.add(diff.imul(weight));
        count++;
      }
    }

    // Avoid center obstacle (turret/shield/earth) - much stronger avoidance
    const distanceFromCenter = position.magnitude;
    const centerAvoidanceRadius = 60; // Larger radius around center to avoid

    if (distanceFromCenter < centerAvoidanceRadius) {
      const awayFromCenter = position.normalize();
      const weight =
        (centerAvoidanceRadius - distanceFromCenter) / centerAvoidanceRadius;
      // Much stronger avoidance force (5x) and exponential scaling
      const exponentialWeight = weight * weight * 5;
      separationForce = separationForce.add(
        awayFromCenter.imul(exponentialWeight),
      );
      count++;
    }

    if (count > 0) {
      separationForce = separationForce
        .imul(1 / count)
        .normalize()
        .imul(maxForce);
    }

    return { force: separationForce, arrived: false };
  }
}

// Extend BaseEnemy interface to include wander angle
declare module "../BaseEnemy" {
  interface BaseEnemy {
    wanderAngle?: number;
  }
}
